import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { TextField } from '../../components/Field';
import { useSignatures } from '../mail/queries';
import { useCalendars } from '../sla/queries';
import { useAssignableUsers } from '../users/queries';
import { toInputValue, type FieldSpec, type ReferenceSource } from './fields';
import { Select } from '../../components/Select';

interface MasterDataFormProps {
  specs: FieldSpec[];
  initialValues: Record<string, unknown>;
  /** Beim Aendern verschwinden Felder, die der Server nur beim Anlegen annimmt. */
  isEdit: boolean;
  busy: boolean;
  error: unknown;
  submitLabel: string;
  onSubmit: (values: Record<string, unknown>) => void;
  onCancel: () => void;
}

/**
 * Die Maske einer Stammdatenart, aufgebaut aus ihrer Feldbeschreibung.
 *
 * Ohne React Hook Form: Die Felder stehen erst zur Laufzeit fest, und ein
 * Formularmodell, das sich bei jedem Wechsel der Stammdatenart neu aufbaut,
 * waere hier mehr Umstand als Gewinn. Die Pruefung liegt ohnehin im Backend
 * -- die Oberflaeche erspart nur den Weg zum Server.
 */
export function MasterDataForm({
  specs,
  initialValues,
  isEdit,
  busy,
  error,
  submitLabel,
  onSubmit,
  onCancel,
}: MasterDataFormProps) {
  const { t } = useTranslation();
  const [values, setValues] = useState<Record<string, unknown>>(initialValues);

  const visible = specs.filter((spec) => !(isEdit && spec.createOnly));

  const update = (name: string, value: unknown) => {
    setValues((current) => ({ ...current, [name]: value }));
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(values);
      }}
      noValidate
    >
      {error ? <ErrorNotice error={error} /> : null}

      {visible.map((spec) => {
        const label = t(spec.labelKey);
        const hint = spec.hintKey ? t(spec.hintKey) : undefined;
        const value = values[spec.name];

        if (spec.kind === 'boolean') {
          return (
            <div className="field" key={spec.name}>
              <label className="row" style={{ gap: 'var(--space-2)' }}>
                <input
                  type="checkbox"
                  checked={Boolean(value)}
                  onChange={(event) => {
                    update(spec.name, event.target.checked);
                  }}
                />
                <span className="field__label" style={{ margin: 0 }}>
                  {label}
                </span>
              </label>
              {hint ? <span className="field__hint">{hint}</span> : null}
            </div>
          );
        }

        if (spec.kind === 'reference') {
          return (
            <ReferenceField
              key={spec.name}
              spec={spec}
              value={toInputValue(value)}
              label={label}
              hint={hint}
              onChange={(next) => {
                // Leere Auswahl heisst "keine Angabe" und nicht "leerer Text":
                // Der Server erwartet eine Kennung oder ``null``.
                update(spec.name, next === '' ? null : next);
              }}
            />
          );
        }

        if (spec.kind === 'select') {
          return (
            <div className="field" key={spec.name}>
              <label className="field__label" htmlFor={`field-${spec.name}`}>
                {label}
              </label>
              <Select
                id={`field-${spec.name}`}
                className="field__control"
                value={toInputValue(value)}
                onChange={(event) => {
                  update(spec.name, event.target.value);
                }}
              >
                {spec.options?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </Select>
              {hint ? <span className="field__hint">{hint}</span> : null}
            </div>
          );
        }

        return (
          <TextField
            key={spec.name}
            label={label}
            hint={hint}
            type={spec.kind === 'number' ? 'number' : spec.kind === 'color' ? 'color' : 'text'}
            required={spec.required}
            min={spec.min}
            max={spec.max}
            value={toInputValue(value)}
            onChange={(event) => {
              update(
                spec.name,
                spec.kind === 'number' ? Number(event.target.value) : event.target.value,
              );
            }}
          />
        );
      })}

      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <Button variant="ghost" onClick={onCancel}>
          {t('app.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={busy}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

interface ReferenceFieldProps {
  spec: FieldSpec;
  value: string;
  label: string;
  hint: string | undefined;
  onChange: (value: string) => void;
}

/**
 * Ein Auswahlfeld, dessen Werte vom Server kommen -- Bearbeiter, Signaturen,
 * Kalender.
 *
 * Alle drei Abfragen laufen, gleich welche Herkunft gefragt ist. Das ist
 * Absicht: Ein bedingter Hook waere ein Regelverstoss, und die drei Listen
 * sind klein, zwischengespeichert und stehen in derselben Maske ohnehin
 * mehrfach nebeneinander.
 */
function ReferenceField({ spec, value, label, hint, onChange }: ReferenceFieldProps) {
  const { t } = useTranslation();

  const users = useAssignableUsers();
  const signatures = useSignatures();
  const calendars = useCalendars();

  const options: { id: string; label: string }[] = optionsFor(spec.source, {
    user: (users.data ?? []).map((entry) => ({ id: entry.id, label: entry.full_name })),
    signature: (signatures.data ?? []).map((entry) => ({ id: entry.id, label: entry.name })),
    calendar: (calendars.data ?? []).map((entry) => ({ id: entry.id, label: entry.name })),
  });

  return (
    <div className="field">
      <label className="field__label" htmlFor={`field-${spec.name}`}>
        {label}
      </label>
      <Select
        id={`field-${spec.name}`}
        className="field__control"
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        <option value="">{t(spec.emptyLabelKey ?? 'masterdata.noSelection')}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </Select>
      {hint ? <span className="field__hint">{hint}</span> : null}
    </div>
  );
}

function optionsFor(
  source: ReferenceSource | undefined,
  lists: Record<ReferenceSource, { id: string; label: string }[]>,
): { id: string; label: string }[] {
  return source ? lists[source] : [];
}
